"use strict";
/**
 * Slots a step derives from its own configuration: the input variables of a
 * code step, the output variables of a code step and the fields of a JSON
 * extract step. The workflow commands write them when a configuration
 * changes; the server's definition validator checks published outputs
 * against them.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.dynamicInputSlotsFromConfig = dynamicInputSlotsFromConfig;
exports.dynamicOutputSlotsFromConfig = dynamicOutputSlotsFromConfig;
function stepId(step) {
    return step['id'] ?? '';
}
function dynamicInputSlotsFromConfig(config) {
    const raw = config['inputVariables'];
    const parsed = typeof raw === 'string'
        ? (() => {
            try {
                return JSON.parse(raw);
            }
            catch {
                return [];
            }
        })()
        : raw;
    if (!Array.isArray(parsed))
        return [];
    return parsed.flatMap(value => {
        if (!value || typeof value !== 'object')
            return [];
        const record = value;
        const id = record['id'];
        const name = record['name'];
        if (typeof id !== 'string' || typeof name !== 'string')
            return [];
        return [
            {
                id,
                name,
                labelsI18n: dynamicSlotLabels(record, name),
                type: typeof record['type'] === 'string' ? record['type'] : 'string',
                required: record['required'] === true,
                bindable: true,
                ...(typeof record['description'] === 'string'
                    ? { description: record['description'] }
                    : {}),
            },
        ];
    });
}
function dynamicOutputSlotsFromConfig(step, config) {
    if (String(step['type'] ?? '').toLowerCase() === 'json_extract') {
        const fields = Array.isArray(config['fields']) ? config['fields'] : [];
        const stepRef = stepId(step);
        const fieldSlots = fields.flatMap(value => {
            if (!value || typeof value !== 'object')
                return [];
            const record = value;
            const name = record['outputName'];
            const stableFieldId = record['id'] ?? record['outputName'];
            const sourceFieldId = record['outputFieldId'] ?? record['outputName'];
            if (typeof name !== 'string' ||
                name.trim() === '' ||
                typeof stableFieldId !== 'string' ||
                stableFieldId.trim() === '' ||
                typeof sourceFieldId !== 'string' ||
                sourceFieldId.trim() === '') {
                return [];
            }
            const normalizedStableFieldId = stableFieldId.trim();
            const normalizedSourceFieldId = sourceFieldId.trim();
            const slotId = normalizedStableFieldId.startsWith(`field-${stepRef}-`)
                ? normalizedStableFieldId
                : `field-${stepRef}-${normalizedStableFieldId}`;
            const configuredType = typeof record['type'] === 'string' ? record['type'] : 'json';
            const type = configuredType === 'string' ? 'text' : configuredType;
            return [
                {
                    id: slotId,
                    name: name.trim(),
                    labelsI18n: dynamicSlotLabels(record, name.trim()),
                    sourceFieldId: normalizedSourceFieldId,
                    type,
                    required: record['required'] === true,
                },
            ];
        });
        if (config['includeRest'] !== true)
            return fieldSlots;
        const restOutputName = typeof config['restOutputName'] === 'string' &&
            config['restOutputName'].trim() !== ''
            ? config['restOutputName'].trim()
            : 'rest';
        const restSlotId = restOutputName.startsWith(`field-${stepRef}-`)
            ? restOutputName
            : `field-${stepRef}-${restOutputName}`;
        return [
            ...fieldSlots,
            {
                id: restSlotId,
                name: restOutputName,
                labelsI18n: { en: restOutputName },
                sourceFieldId: restOutputName,
                type: 'json',
                required: false,
            },
        ];
    }
    const raw = config['outputVariables'];
    const parsed = typeof raw === 'string'
        ? (() => {
            try {
                return JSON.parse(raw);
            }
            catch {
                return [];
            }
        })()
        : raw;
    if (!Array.isArray(parsed))
        return [];
    return parsed.flatMap(value => {
        if (!value || typeof value !== 'object')
            return [];
        const record = value;
        const id = record['id'];
        const name = record['name'];
        if (typeof id !== 'string' || typeof name !== 'string')
            return [];
        const stepRef = stepId(step);
        const slotId = id.startsWith(`field-${stepRef}-`)
            ? id
            : `field-${stepRef}-${id}`;
        return [
            {
                id: slotId,
                name,
                labelsI18n: dynamicSlotLabels(record, name),
                sourceFieldId: id,
                type: typeof record['type'] === 'string' ? record['type'] : 'string',
                required: false,
                ...(typeof record['description'] === 'string'
                    ? { description: record['description'] }
                    : {}),
            },
        ];
    });
}
function dynamicSlotLabels(record, fallback) {
    const raw = record['labelsI18n'];
    const labels = raw && typeof raw === 'object' && !Array.isArray(raw)
        ? Object.fromEntries(Object.entries(raw).flatMap(([locale, value]) => typeof value === 'string' && value.trim() !== ''
            ? [[locale, value.trim()]]
            : []))
        : {};
    return {
        ...labels,
        en: typeof labels['en'] === 'string' && labels['en'].trim() !== ''
            ? labels['en']
            : fallback,
    };
}
